import React, { useEffect, useState } from 'react';
import { ColumnProps, SortOrder } from 'antd/lib/table';
import { Popover } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProductMatchInfo from 'src/types/ProductMatcheInfo';
import { CURRENCY } from 'src/enums';
import { Sorting } from 'src/types/Sorting';
import MATCH_TYPE from 'src/enums/matchType';
import FormattedNumber from 'src/components/common/FormattedNumber/FormattedNumber';
import RemoveMatchButton from 'src/components/ProductDetails/RemoveMatchButton';
import Image from 'src/components/common/Image';
import { getVarianceColor } from 'src/utils';
import BenchmarkMatchButton from 'src/components/ProductDetails/BenchmarkMatchButton';
import UnBenchmarkMatchButton from 'src/components/ProductDetails/UnBenchmarkMatchButton';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import styles from '../MatchDetailsTable.module.less';

type UserPermission = { [key: string]: boolean };

export interface ColumnConfig extends ColumnProps<ProductMatchInfo> {
  customColRenderer?: (record: ProductMatchInfo, i: number, permissions: UserPermission) => React.ReactNode;
}
let showProductImage: string | null = 'false';
const sortOrder = (sortedInfo: Sorting<ProductMatchInfo> | null, key: keyof ProductMatchInfo): SortOrder | boolean => {
  return sortedInfo && sortedInfo.field === key ? sortedInfo.order : false;
};
const ownerRender = (record: ProductMatchInfo): React.ReactNode => (
  <span className={styles.imageContainer}>
    <span className={styles.benchmark}>Benchmark</span>
    <Image url={record.ownerImage} alt={record.ownerName || ''} />
  </span>
);
const imageRender = (record: ProductMatchInfo): React.ReactNode => (
  <span className={styles.imageContainer}>
    {showProductImage === 'true' && <Image url={record.imageURL} alt={record.name || ''} />}
  </span>
);
const matchTypeRender = (record: ProductMatchInfo): React.ReactNode =>
  record.matchType ? (MATCH_TYPE[record.matchType] ? MATCH_TYPE[record.matchType].toUpperCase() : 'n/a') : 'n/a';
const priceRender = (record: ProductMatchInfo): React.ReactNode => (
  <>
    <FormattedNumber type="currency" currency={record.currency} value={record.price} />
  </>
);
const lowerpriceRender = (record: ProductMatchInfo): React.ReactNode => (
  <>
    <FormattedNumber type="currency" currency={record.currency} value={record.lowestPrice} />
    <Popover
      placement="right"
      content={
        <div className={styles.popover}>
          {record.lowestPriceInfo
            ? record.lowestPriceInfo
              .replaceAll('$', `${CURRENCY[record.currency] || record.currency} `)
              .replaceAll(' :', ': ')
              .replaceAll(',', '\n')
            : 'N/A'}
        </div>
      }
      trigger={'hover'}
    >
      <FontAwesomeIcon icon={['fal', 'info-circle']} className={styles.infoIcon} />
    </Popover>
  </>
);
const varianceRender = (key: 'variance' | 'lowestPriceVariance', tooltip: string) => (record: ProductMatchInfo) => {
  if (!record[key] || !record.varianceMetric) {
    return 'n/a';
  }
  const color = getVarianceColor(record[key]);
  const variance = Number.parseFloat(record[key]).toFixed();
  const content = <div className={styles.popover}>{tooltip}</div>;
  return (
    <>
      <Popover content={content} trigger={'hover'} placement="left">
        <span style={{ color }}>{`${variance} ${record.varianceMetric}`}</span>
      </Popover>
    </>
  );
};

const standardVarianceRender = varianceRender(
  'variance',
  'This indicates the variance between the source and competitor product regular price'
);
const lowerVarianceRender = varianceRender(
  'lowestPriceVariance',
  'This indicates the variance between the source and competitor product lowest price'
);

const actionRender =
  (onSelectRows: () => void) =>
    (record: ProductMatchInfo, i: number, permissions: UserPermission): React.ReactNode =>
    (
      <>
        {record.productURL && (
          <a href={record.productURL} className={styles.actionItem} rel="noopener noreferrer" target={'_blank'}>
            <FontAwesomeIcon icon={['fal', 'globe']} />
          </a>
        )}
        {!permissions.isReadOnlyMatch && (
          <RemoveMatchButton
            className={styles.action_item}
            matchItems={[record]}
            tooltipPlacement={'left'}
            key={`index_${record.ID + i++}`}
            onSelectedRow={onSelectRows}
          />
        )}

        {!permissions.isReadOnlyBenchmark &&
          (record.benchmarkMatch !== '1' ? (
            <BenchmarkMatchButton
              className={styles.action_item}
              matchItems={[record]}
              tooltipPlacement={'left'}
              key={`index1_${record.name}`}
              onSelectedRow={onSelectRows}
            />
          ) : (
            <UnBenchmarkMatchButton
              className={styles.action_item}
              matchItems={[record]}
              tooltipPlacement={'left'}
              key={`index2_${record.productId}`}
              onSelectedRow={onSelectRows}
            />
          ))}
      </>
    );

const getColumns = (
  sorting: Sorting<ProductMatchInfo>,
  priceTypeIdsUpdateS: string,
  permissions: UserPermission,
  onSelectRows: () => void
): ColumnConfig[] => {
  let columns: ColumnConfig[] = [
    {
      title: 'Site',
      dataIndex: 'ownerName',
      key: 'ownerName',
      width: 120,
      className: styles.ownerColumn,
      customColRenderer: ownerRender,
      sorter: true,
      sortOrder: sortOrder(sorting, 'ownerName'),
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: '',
      dataIndex: 'imageURL',
      key: 'imageURL',
      width: 120,
      className: styles.productImageColumn,
      customColRenderer: imageRender,
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortOrder(sorting, 'name'),
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Match Type',
      dataIndex: 'matchType',
      key: 'matchType',
      width: 120,
      customColRenderer: matchTypeRender,
      className: styles.matchTypeColumn,
      sorter: true,
      sortOrder: sortOrder(sorting, 'matchType'),
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      align: 'center',
      title: 'Regular Price',
      dataIndex: 'price',
      key: 'price',
      width: 125,
      customColRenderer: priceRender,
      className: styles.priceColumn,
      sorter: true,
      sortOrder: sortOrder(sorting, 'price'),
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      align: 'center',
      title: 'Lowest Price',
      dataIndex: 'lowestPrice',
      key: 'lowestPrice',
      width: 125,
      customColRenderer: lowerpriceRender,
      className: styles.priceColumn,
      sorter: true,
      sortOrder: sortOrder(sorting, 'lowestPrice'),
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      align: 'center',
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      width: 80,
      customColRenderer: priceTypeIdsUpdateS === '1' ? standardVarianceRender : lowerVarianceRender,
      className: styles.varianceColumn,
      sorter: true,
      sortOrder: sortOrder(sorting, 'variance'),
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      align: 'right',
      title: '',
      key: 'actions',
      customColRenderer: actionRender(onSelectRows),
      className: styles.actionsColumn,
      width: 80,
    },
  ];
  columns = columns.map((column: ColumnConfig): ColumnConfig => {
    if (column.customColRenderer) {
      const renderer = column.customColRenderer;
      column.render = (_: unused, record: ProductMatchInfo, i: number): React.ReactNode => {
        return renderer(record, i, permissions);
      };
    }
    return column;
  });

  return columns;
};

const useMatchDetailsTableColumns = (
  sorting: Sorting<ProductMatchInfo>,
  priceTypeIdsUpdateS: string,
  permissions: UserPermission,
  onSelectRows: () => void
): [ColumnConfig[]] => {
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const { priceType } = useQueryUrlParams();
  const priceTypeIdsUpdate: string = priceType !== undefined ? priceType : '-1';
  const [, setPriceTypeIdsUpdate] = useState(priceTypeIdsUpdate);
  useEffect(() => {
    if (sorting) {
      setColumns(getColumns(sorting, priceTypeIdsUpdateS, permissions, onSelectRows));
      setPriceTypeIdsUpdate(priceTypeIdsUpdate);
      showProductImage = localStorage.getItem('display_product_image');
    }
  }, [sorting, priceTypeIdsUpdateS, priceTypeIdsUpdate, showProductImage]);
  return [columns];
};

export default useMatchDetailsTableColumns;
